import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { getModuleIcon, getFamiliesForModule, getAllowedTiersForGroup, type ModuleId } from '../../../content';
import { getFamilyMasteryKey } from '../../../mastery';
import type { GroupId } from '../../../config/systemConfig';
import { Card } from '../../../components/ui/Card';
import { EmptyState } from '../../../components/shared/EmptyState';
import { StudentData } from './StudentDetailPanel';
import { PageLayout } from '../../shared/PageLayout';
import { getClassModuleStats } from '../../../utils/activityHelpers';
import { humanizeFamilyId } from '../../../content';
import { SubjectModuleRow } from '../../shared/SubjectModuleRow';
import { SubjectsSummaryHero } from '../../shared/SubjectsSummaryHero';
import { getMasteryBand, DEVELOPING_THRESHOLD } from '../../shared/masteryBands';
interface SubjectsPageProps {
  students: StudentData[];
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: string) => number;
  onSelectStudent: (studentId: string) => void;
}
type SortOption = 'attention' | 'strong' | 'alpha';
function getClassFamilyStats(students: StudentData[], moduleId: string, selectedGroup: number) {
  const families = getFamiliesForModule(moduleId as ModuleId);
  const allowedTiers = getAllowedTiersForGroup(selectedGroup as GroupId);
  const relevantFamilies = families.filter(f => allowedTiers.has(f.tier));
  return relevantFamilies.map(f => {
    const key = getFamilyMasteryKey(f.familyId, selectedGroup);
    let totalMastery = 0;
    let practicedCount = 0;
    const strugglingStudents: Array<{
      id: string;
      name: string;
      mastery: number;
    }> = [];
    students.forEach(s => {
      const fm = s.data.familyMastery?.[key];
      if (fm && fm.attempts > 0) {
        practicedCount++;
        totalMastery += fm.masteryScore;
        if (fm.masteryScore < DEVELOPING_THRESHOLD) {
          strugglingStudents.push({
            id: s.id,
            name: s.name,
            mastery: Math.round(fm.masteryScore)
          });
        }
      }
    });
    const avgMastery = practicedCount > 0 ? Math.round(totalMastery / practicedCount) : 0;
    return {
      familyId: f.familyId,
      label: humanizeFamilyId(f.familyId),
      avgMastery,
      practicedCount,
      strugglingStudents: strugglingStudents.sort((a, b) => a.mastery - b.mastery)
    };
  }).filter(f => f.practicedCount > 0).sort((a, b) => b.avgMastery - a.avgMastery);
}
function ExpandedModulePanelContent({
  moduleId,
  students,
  selectedGroup,
  strugglingStudents,
  onSelectStudent
}: {
  moduleId: string;
  students: StudentData[];
  selectedGroup: number;
  strugglingStudents: Array<{
    id: string;
    name: string;
    mastery: number;
  }>;
  onSelectStudent: (id: string) => void;
}) {
  const [selectedFamily, setSelectedFamily] = useState<string | null>(null);
  const familyStats = useMemo(() => {
    return getClassFamilyStats(students, moduleId, selectedGroup);
  }, [students, moduleId, selectedGroup]);
  return <div className="pl-4 sm:pl-18 pr-4 py-4 space-y-6" data-id="element-9569">
      {/* Family Table */}
      {familyStats.length > 0 && <div className="space-y-3" data-id="element-9570">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider" data-id="element-9571">
            Onderwerpen ({familyStats.length})
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-9572">
            <div className="divide-y divide-slate-100" data-id="element-9573">
              {familyStats.map(f => {
            const isFamilyExpanded = selectedFamily === f.familyId;
            return <div key={f.familyId} className="flex flex-col" data-id="element-9574">
                    <div className="p-3 flex items-center gap-4 hover:bg-slate-50 cursor-pointer transition-colors" role="button" tabIndex={0} aria-expanded={isFamilyExpanded} onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedFamily(isFamilyExpanded ? null : f.familyId);
                }
              }} onClick={() => setSelectedFamily(isFamilyExpanded ? null : f.familyId)} data-id="element-9575">
                      <div className="flex-1 min-w-0" data-id="element-9576">
                        <div className="text-sm font-bold text-slate-900 truncate" data-id="element-9577">
                          {f.label}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-9578">
                          {f.practicedCount} leerlingen
                        </div>
                      </div>
                      <div className="w-32 flex items-center gap-2" data-id="element-9579">
                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-9580">
                          <div className={`h-full rounded-full ${getMasteryBand(f.avgMastery).barClass}`} style={{
                      width: `${Math.max(2, f.avgMastery)}%`
                    }} data-id="element-9581" />
                        </div>
                        <div className={`w-8 text-right text-xs font-bold ${getMasteryBand(f.avgMastery).textClass}`} data-id="element-9582">
                          {f.avgMastery}%
                        </div>
                      </div>
                      <div className="w-6 flex justify-end text-slate-400" data-id="element-9583">
                        {isFamilyExpanded ? <ChevronUp className="w-4 h-4" data-id="element-9584" /> : <ChevronDown className="w-4 h-4" data-id="element-9585" />}
                      </div>
                    </div>

                    <AnimatePresence data-id="element-9586">
                      {isFamilyExpanded && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="overflow-hidden bg-slate-50/50 border-t border-slate-100" data-id="element-9587">
                          <div className="p-3 pl-4" data-id="element-9588">
                            {f.strugglingStudents.length === 0 ? <div className="text-xs font-medium text-emerald-600" data-id="element-9589">
                                Geen leerlingen met moeite.
                              </div> : <div className="space-y-2" data-id="element-9590">
                                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider" data-id="element-9591">
                                  Aandacht nodig ({f.strugglingStudents.length})
                                </div>
                                <div className="flex flex-wrap gap-2" data-id="element-9592">
                                  {f.strugglingStudents.map(s => {
                          const initial = s.name.charAt(0).toUpperCase();
                          return <div key={s.id} role="button" tabIndex={0} onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              onSelectStudent(s.id);
                            }
                          }} onClick={e => {
                            e.stopPropagation();
                            onSelectStudent(s.id);
                          }} className="flex items-center gap-2 px-2 py-1.5 bg-white border border-slate-200 rounded-lg hover:border-indigo-200 cursor-pointer shadow-sm transition-colors" data-id="element-9593">
                                        <div className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-[10px] font-bold" data-id="element-9594">
                                          {initial}
                                        </div>
                                        <span className="text-xs font-bold text-slate-900" data-id="element-9595">
                                          {s.name}
                                        </span>
                                        <span className="text-xs font-black text-rose-600" data-id="element-9596">
                                          {s.mastery}%
                                        </span>
                                      </div>;
                        })}
                                </div>
                              </div>}
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>;
          })}
            </div>
          </div>
        </div>}

      {/* Module Struggling Students */}
      {strugglingStudents.length === 0 ? <div className="text-sm font-medium text-emerald-600 flex items-center gap-2" data-id="element-9597">
          Alle leerlingen op schema voor dit vak ✓
        </div> : <div className="space-y-2" data-id="element-9598">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-9599">
            Aandacht nodig voor hele vak ({strugglingStudents.length})
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2" data-id="element-9600">
            {strugglingStudents.slice(0, 5).map(s => {
          const initial = s.name.charAt(0).toUpperCase();
          return <div key={s.id} role="button" tabIndex={0} onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onSelectStudent(s.id);
            }
          }} onClick={e => {
            e.stopPropagation();
            onSelectStudent(s.id);
          }} className="flex items-center justify-between p-2 rounded-lg bg-white border border-slate-200 hover:border-indigo-200 cursor-pointer transition-colors" data-id="element-9601">
                  <div className="flex items-center gap-2" data-id="element-9602">
                    <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold" data-id="element-9603">
                      {initial}
                    </div>
                    <span className="text-sm font-bold text-slate-900 truncate max-w-[100px]" data-id="element-9604">
                      {s.name}
                    </span>
                  </div>
                  <div className="text-xs font-black text-rose-600" data-id="element-9605">
                    {s.mastery}%
                  </div>
                </div>;
        })}
          </div>
          {strugglingStudents.length > 5 && <div className="text-xs font-medium text-slate-500 mt-2" data-id="element-9606">
              + {strugglingStudents.length - 5} meer
            </div>}
        </div>}
    </div>;
}
export function SubjectsPage({
  students,
  selectedGroup,
  getModuleMastery,
  onSelectStudent
}: SubjectsPageProps) {
  const [selectedModule, setSelectedModule] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('attention');
  const data = useMemo(() => {
    if (students.length === 0) return null;
    const moduleStats = getClassModuleStats(students, selectedGroup, getModuleMastery);
    const sortedStats = [...moduleStats].sort((a, b) => {
      if (sortBy === 'attention') {
        // Primary sort: declining trend
        if (a.trend === 'down' && b.trend !== 'down') return -1;
        if (b.trend === 'down' && a.trend !== 'down') return 1;
        // Secondary sort: lowest mastery
        return a.avgMastery - b.avgMastery;
      }
      if (sortBy === 'strong') {
        return b.avgMastery - a.avgMastery;
      }
      // alpha
      return a.moduleTitle.localeCompare(b.moduleTitle);
    });
    return {
      moduleStats: sortedStats
    };
  }, [students, getModuleMastery, selectedGroup, sortBy]);
  if (!data) {
    return <PageLayout title="Vakken" description="Analyse per vak" data-id="element-9607">
        <EmptyState title="Geen data beschikbaar" message="Er is nog geen data beschikbaar voor deze klas." emotion="thinking" colorScheme="slate" data-id="element-9608" />
      </PageLayout>;
  }
  const classAverage = data.moduleStats.length > 0 ? Math.round(data.moduleStats.reduce((acc, stat) => acc + stat.avgMastery, 0) / data.moduleStats.length) : 0;
  const attentionCount = data.moduleStats.filter(stat => stat.avgMastery < DEVELOPING_THRESHOLD).length;
  const handleAttentionClick = () => {
    // Find first module with attention needed
    const firstAttentionModule = data.moduleStats.find(stat => stat.avgMastery < DEVELOPING_THRESHOLD);
    if (firstAttentionModule) {
      setSelectedModule(firstAttentionModule.moduleId);
      // Small delay to allow expansion before scrolling
      setTimeout(() => {
        const el = document.getElementById(`module-${firstAttentionModule.moduleId}`);
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 100);
    }
  };
  return <PageLayout title="Vakken" description="Analyse per vak" data-id="element-9609">
      <div className="max-w-5xl mx-auto space-y-6 pb-12" data-id="element-9610">
        <SubjectsSummaryHero variant="teacher" studentCount={students.length} classAverage={classAverage} attentionCount={attentionCount} onAttentionClick={handleAttentionClick} data-id="element-9611" />

        <div className="flex items-center justify-between mt-8 mb-4" data-id="element-9612">
          <h3 className="text-lg font-black text-slate-900" data-id="element-9613">Alle vakken</h3>
          <select value={sortBy} onChange={e => setSortBy(e.target.value as SortOption)} className="text-sm border-slate-200 rounded-lg bg-white text-slate-700 font-medium focus:ring-indigo-500 focus:border-indigo-500 py-1.5 pl-3 pr-8 shadow-sm" data-id="element-9614">
            <option value="attention" data-id="element-9615">Aandacht eerst</option>
            <option value="strong" data-id="element-9616">Sterk eerst</option>
            <option value="alpha" data-id="element-9617">Alfabetisch</option>
          </select>
        </div>

        <Card className="border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-9618">
          <div className="divide-y divide-slate-100" data-id="element-9619">
            {data.moduleStats.map(stat => {
            const IconComp = getModuleIcon(stat.moduleIcon);
            const isExpanded = selectedModule === stat.moduleId;
            const strugglingStudents = students.map(s => ({
              ...s,
              mastery: getModuleMastery(s.id, stat.moduleId)
            })).filter(s => s.mastery < DEVELOPING_THRESHOLD).sort((a, b) => a.mastery - b.mastery);
            return <div key={stat.moduleId} id={`module-${stat.moduleId}`} data-id="element-9620">
                  <SubjectModuleRow variant="teacher" moduleId={stat.moduleId} moduleTitle={stat.moduleTitle} moduleIcon={<IconComp className="w-5 h-5" data-id="element-9622" />} mastery={stat.avgMastery} trend={stat.trend} distribution={{
                strong: stat.distribution.strong,
                solid: stat.distribution.solid,
                developing: stat.distribution.developing,
                attention: stat.distribution.attention,
                total: stat.distribution.strong + stat.distribution.solid + stat.distribution.developing + stat.distribution.attention
              }} practicedCount={stat.practicedCount} isExpanded={isExpanded} onToggle={() => setSelectedModule(isExpanded ? null : stat.moduleId)} data-id="element-9621">
                    <ExpandedModulePanelContent moduleId={stat.moduleId} students={students} selectedGroup={selectedGroup} strugglingStudents={strugglingStudents} onSelectStudent={onSelectStudent} data-id="element-9623" />
                  </SubjectModuleRow>
                </div>;
          })}
          </div>
          <div className="bg-slate-50 p-3 border-t border-slate-100 flex flex-wrap justify-center gap-x-6 gap-y-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider" data-id="element-9624">
            <div className="flex items-center gap-1.5" data-id="element-9625">
              <div className="w-2.5 h-2.5 rounded-sm bg-emerald-500" data-id="element-9626" /> Sterk
            </div>
            <div className="flex items-center gap-1.5" data-id="element-9627">
              <div className="w-2.5 h-2.5 rounded-sm bg-indigo-500" data-id="element-9628" /> Goed op
              weg
            </div>
            <div className="flex items-center gap-1.5" data-id="element-9629">
              <div className="w-2.5 h-2.5 rounded-sm bg-amber-500" data-id="element-9630" /> Aan het
              groeien
            </div>
            <div className="flex items-center gap-1.5" data-id="element-9631">
              <div className="w-2.5 h-2.5 rounded-sm bg-rose-500" data-id="element-9632" /> Aandacht
            </div>
          </div>
        </Card>
      </div>
    </PageLayout>;
}